import {
  EntityAvatar,
  EntityAvatarFallback,
  EntityAvatarImage,
  Text,
  toColor,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

// `xs` is an 18px avatar — between the library's 16/20 avatar steps, sized for
// the 28px top-bar selector pill. `md` is a 40px tile — between the 32/48
// steps — for the workspace menu's identity header, where the tile leads a
// two-line name + details column. `lg` (48px) is the hero tile on a full-page
// workspace screen (the invite screen), where the mark is the subject.
type WorkspaceAvatarSize = "2xs" | "xs" | "sm" | "default" | "md" | "lg";

/** Off-scale rungs ride the nearest library size plus a measured override. */
const OFF_SCALE_CLASS: Partial<Record<WorkspaceAvatarSize, string>> = {
  xs: "size-4.5",
  md: "size-10",
};

/** The library rung each workspace size renders on. */
const ENTITY_SIZE: Record<WorkspaceAvatarSize, "2xs" | "xs" | "sm" | "default" | "lg"> = {
  "2xs": "2xs",
  xs: "xs",
  sm: "sm",
  default: "default",
  md: "default",
  lg: "lg",
};

type WorkspaceAvatarProps = Omittable<{
  name: string;
  logo?: string | null;
  /** The persisted key color (free-form string from the API — narrowed here;
   *  there is no "no color" state, unset renders the default grey tile). */
  avatarColor?: string | null;
  size?: WorkspaceAvatarSize;
}>;

const TEXT_PROPS_MAP: Record<
  WorkspaceAvatarSize,
  { size: "2xs" | "xs" | "sm" | "lg" | "xl"; weight: "bold" }
> = {
  lg: { size: "xl", weight: "bold" },
  md: { size: "lg", weight: "bold" },
  default: { size: "sm", weight: "bold" },
  sm: { size: "xs", weight: "bold" },
  xs: { size: "2xs", weight: "bold" },
  "2xs": { size: "2xs", weight: "bold" },
};

export function WorkspaceAvatar({
  name,
  logo,
  avatarColor,
  size = "sm",
}: WorkspaceAvatarProps) {
  const text = TEXT_PROPS_MAP[size];
  return (
    <EntityAvatar
      size={ENTITY_SIZE[size]}
      className={OFF_SCALE_CLASS[size]}
      // Squared, always — the workspace mark is a tile, which is what keeps it
      // legible next to the circular user avatar in the same chrome.
      shape="square"
      alt={name}
    >
      {logo ? <EntityAvatarImage src={logo} alt={name} /> : null}
      {/* One character, always — the mark is a monogram, not initials — on a
        * key-color fill under a subtle diagonal sheen. The character keeps the
        * name's own case: a lowercase workspace name wears a lowercase mark.
        * Grey is the resting default; there is no colorless workspace tile. */}
      <EntityAvatarFallback color={toColor(avatarColor) ?? "grey"} gradient>
        <Text size={text.size} weight={text.weight}>
          {name.trim().charAt(0)}
        </Text>
      </EntityAvatarFallback>
    </EntityAvatar>
  );
}
